import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';

import { useIsAdmin } from '../auth/useIsAdmin';
import { useTopLayers } from '../map-gl/useTopLayers';
import { blocksIn, cellOf } from './blockGrid';
import './BlockGridOverlay.css';

/**
 * The 10 km block grid, drawn over whatever is already on the map.
 *
 * The blocks in view are computed on move, not fetched — see blockGrid.js. About
 * 25,000 of them cover WA, so there is nothing to download and nothing to keep in
 * sync with the pipeline: the same id names the same square in both.
 *
 * Below MIN_ZOOM a block is a few pixels across and the grid is a grey wash, so it
 * is not drawn at all rather than drawn uselessly.
 */
const MIN_ZOOM = 9;
const LABEL_ZOOM = 11;
const EMPTY = { type: 'FeatureCollection', features: [] };
const SRC = 'block-grid';
/** Draw order, and what useTopLayers keeps above the rest of the map. */
const ORDER = ['block-grid-casing', 'block-grid-line', 'block-grid-label'];

export function BlockGridOverlay({ overlayVisibility }) {
  const isAdmin = useIsAdmin();
  const visible = !!overlayVisibility?.blocks && isAdmin;
  const { current: map } = useMap();

  const [fc, setFc] = useState(EMPTY);
  const [picked, setPicked] = useState(null);
  const [copied, setCopied] = useState(false);

  const recompute = useCallback(() => {
    if (!map) return;
    if (map.getZoom() < MIN_ZOOM) { setFc(EMPTY); return; }
    const b = map.getBounds();
    // A little margin so a slow pan does not show the grid's own edge.
    const pad = 0.15;
    setFc(blocksIn(b.getWest() - pad, b.getSouth() - pad,
                   b.getEast() + pad, b.getNorth() + pad));
  }, [map]);

  useEffect(() => {
    if (!map || !visible) { setFc(EMPTY); setPicked(null); return undefined; }
    recompute();
    map.on('moveend', recompute);
    map.on('zoomend', recompute);
    return () => { map.off('moveend', recompute); map.off('zoomend', recompute); };
  }, [map, visible, recompute]);

  useEffect(() => {
    if (!map || !visible) return undefined;
    const onClick = (e) => {
      // The grid covers everything, so it must never swallow a click meant for a
      // tenement or a sample underneath: it answers only when nothing else did.
      const others = map.queryRenderedFeatures(e.point)
        .filter((f) => f.source !== SRC);
      if (others.length) return;
      setCopied(false);
      setPicked({ lngLat: e.lngLat, cell: cellOf(e.lngLat.lng, e.lngLat.lat) });
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [map, visible]);

  // The grid is a reference frame read over everything else, so it has to sit
  // over everything else. It stays under the prospect marks (rank 20): those
  // are what gets clicked, and a grid line through one is not worth burying it.
  useTopLayers('block-grid', 10, ORDER, map, visible);

  const command = picked ? `./prospect.py init ${picked.cell}` : '';
  const copy = useCallback(() => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(picked.cell).then(() => setCopied(true), () => {});
  }, [picked]);

  const layers = useMemo(() => ([
    // Two passes. A single white hairline disappears into pale lake clay and into
    // the bright red-orange of open ground, which is most of this state; the dark
    // casing under it is what makes the grid readable on satellite imagery.
    { id: ORDER[0], type: 'line', source: SRC, minzoom: MIN_ZOOM,
      paint: { 'line-color': '#0B0F10', 'line-width': 2.4, 'line-opacity': 0.35 } },
    { id: ORDER[1], type: 'line', source: SRC, minzoom: MIN_ZOOM,
      paint: { 'line-color': '#F4F7F5', 'line-width': 1, 'line-opacity': 0.75 } },
    { id: ORDER[2], type: 'symbol', source: SRC, minzoom: LABEL_ZOOM,
      layout: { 'text-field': ['get', 'cell'], 'text-size': 10,
                // The style's glyph server carries Regular only — asking for a
                // weight it does not have renders nothing, silently.
                'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'] },
      paint: { 'text-color': '#F4F7F5', 'text-halo-color': '#0B0F10',
               'text-halo-width': 1.4, 'text-opacity': 0.9 } },
  ]), []);

  if (!visible) return null;

  return (
    <>
      <Source id={SRC} type="geojson" data={fc}>
        {layers.map((l) => <Layer key={l.id} {...l} />)}
      </Source>
      {picked && (
        <Popup longitude={picked.lngLat.lng} latitude={picked.lngLat.lat}
               anchor="bottom" closeOnClick={false} maxWidth="300px"
               onClose={() => setPicked(null)} className="blockgrid-popup">
          <div className="bg-pop">
            <h3>{picked.cell}</h3>
            <p className="bg-sub">10 km block · MGA2020 zone {picked.cell.slice(3, 5)}</p>
            <code>{command}</code>
            <button type="button" onClick={copy}>{copied ? 'Copied' : 'Copy block id'}</button>
          </div>
        </Popup>
      )}
    </>
  );
}

export default BlockGridOverlay;
